/**
 * Affiliate Link Edit Modal
 * 
 * Modal for editing existing campaign links:
 * - Update campaign name
 * - Update referral discount override
 * - Pause/activate link
 * - Cannot change slug (would break existing links)
 */

import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2Icon,
  CopyIcon,
  CheckIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useUpdateLink } from "../hooks/use-affiliate-data";
import type { AffiliateLink, Affiliate } from "../types";

import { API_BASE_URL } from "@/backend/config";

// Use the redirect service URL (via Doppler in production)
// This goes through the API to track clicks, then redirects to /invite/:slug
const REDIRECT_BASE_URL = import.meta.env.VITE_REDIRECT_BASE_URL || API_BASE_URL;

type AffiliateLinkEditModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  link: AffiliateLink | null;
  affiliate: Affiliate;
};

export const AffiliateLinkEditModal = ({
  open,
  onOpenChange,
  link,
  affiliate,
}: AffiliateLinkEditModalProps) => {
  const updateLink = useUpdateLink();
  
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [useCustomDiscount, setUseCustomDiscount] = useState(false);
  const [customDiscount, setCustomDiscount] = useState(0);
  const [copied, setCopied] = useState(false);

  const maxCommission = affiliate.tradingFeeShare * 100; // 40% - total commission
  const adminBaseRebate = affiliate.referralDiscount * 100; // 10% - admin-set MINIMUM

  // Initialize form when link changes
  useEffect(() => {
    if (link && open) {
      setName(link.name);
      setDescription(link.description || "");
      setIsActive(link.isActive);
      
      if (link.referralDiscount !== null) {
        setUseCustomDiscount(true);
        setCustomDiscount(link.referralDiscount * 100);
      } else {
        setUseCustomDiscount(false);
        setCustomDiscount(adminBaseRebate);
      }
    }
  }, [link, open, adminBaseRebate]);

  const handleCopy = async () => {
    if (!link) return;
    const url = `${REDIRECT_BASE_URL}/r/${link.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleSubmit = async () => {
    if (!link) return;

    if (!name.trim()) {
      toast.error("Campaign name is required");
      return;
    }

    try {
      await updateLink.mutateAsync({
        linkId: link.id,
        name: name.trim(),
        referral_discount: useCustomDiscount ? (customDiscount / 100).toString() : "null",
        description: description.trim() || undefined,
        is_active: isActive,
      });
      toast.success("Link updated!");
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update link");
    }
  };

  if (!link) return null;

  const effectiveRebate = useCustomDiscount ? customDiscount : adminBaseRebate;
  
  // Calculate commission breakdown:
  // - Platform always pays the admin base rebate (10%)
  // - If affiliate gives MORE, it comes from their commission
  const extraRebateFromAffiliate = Math.max(0, effectiveRebate - adminBaseRebate);
  const affiliateNetCommission = maxCommission - extraRebateFromAffiliate;
  
  const linkUrl = `${REDIRECT_BASE_URL}/r/${link.slug}`;

  return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>
              Edit Campaign Link
            </DialogTitle>
            <DialogDescription>
              {link.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Link URL (read-only) */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Link URL
              </label>
              <div className="flex gap-2">
                <div className="flex-1 dark:bg-input/30 border-input h-9 rounded-md border px-3 flex items-center text-base md:text-sm truncate">
                  {linkUrl}
                </div>
                <Button
                  variant="outline"
                  onClick={handleCopy}
                  className="shrink-0"
                >
                  {copied ? (
                    <CheckIcon className="size-4 text-emerald-500" />
                  ) : (
                    <CopyIcon className="size-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                The slug cannot be changed to preserve existing links
              </p>
            </div>

            {/* Campaign Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Campaign Name *</label>
              <Input
                placeholder="e.g., Twitter Q1, YouTube Banner"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={50}
              />
            </div>

            {/* Custom Discount Toggle */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">
                  Referral Discount
                </label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setUseCustomDiscount(!useCustomDiscount)}
                  className={cn(
                    "h-7 text-xs",
                    useCustomDiscount && "bg-primary/10 border-primary/30"
                  )}
                >
                  {useCustomDiscount ? "Using Custom" : "Set Custom"}
                </Button>
              </div>

              {useCustomDiscount && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-emerald-500/10 rounded-lg p-3 text-center border border-emerald-500/20">
                      <p className="text-xs text-muted-foreground mb-1">Your Net Commission</p>
                      <p className="text-xl font-bold text-emerald-500">
                        {affiliateNetCommission.toFixed(0)}%
                      </p>
                      {extraRebateFromAffiliate > 0 && (
                        <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">
                          ({maxCommission}% - {extraRebateFromAffiliate}% extra)
                        </p>
                      )}
                      {extraRebateFromAffiliate === 0 && (
                        <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-0.5">
                          (full commission)
                        </p>
                      )}
                    </div>
                    <div className="bg-primary/10 rounded-lg p-3 text-center border border-primary/20">
                      <p className="text-xs text-muted-foreground mb-1">Referral Rebate</p>
                      <p className="text-xl font-bold text-primary">
                        {effectiveRebate.toFixed(0)}%
                      </p>
                      <p className="text-[10px] text-primary/80 mt-0.5">
                        ({adminBaseRebate}% platform{extraRebateFromAffiliate > 0 ? ` + ${extraRebateFromAffiliate}% you` : ""})
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{adminBaseRebate.toFixed(0)}% (platform pays)</span>
                      <span>{maxCommission.toFixed(0)}% (give all)</span>
                    </div>
                    <Slider
                      value={[customDiscount]}
                      onValueChange={([value]) => setCustomDiscount(value)}
                      min={adminBaseRebate}
                      max={maxCommission}
                      step={1}
                      className="w-full"
                    />
                  </div>

                  <div className="flex gap-2 mt-1">
                    {[adminBaseRebate, 15, 20, 25, maxCommission].filter((preset, idx, arr) => 
                      preset >= adminBaseRebate && preset <= maxCommission && arr.indexOf(preset) === idx
                    ).map((preset) => (
                      <button
                        key={preset}
                        onClick={() => setCustomDiscount(preset)}
                        className={cn(
                          "flex-1 py-1.5 text-xs font-medium rounded border transition-colors",
                          customDiscount === preset
                            ? "border-buy bg-buy/10 text-buy"
                            : "border-border bg-muted/30 text-muted-foreground hover:border-muted-foreground hover:text-foreground"
                        )}
                      >
                        {preset}%
                      </button>
                    ))}
                  </div>
                </>
              )}

              {!useCustomDiscount && (
                <p className="text-xs text-muted-foreground">
                  Using default: <span className="font-medium">{adminBaseRebate.toFixed(0)}%</span> (platform-paid)
                </p>
              )}
              
            </div>

            {/* Notes + Active Toggle in same row */}
            <div className="grid grid-cols-4 gap-4 items-end">
              <div className="col-span-3 space-y-2">
                <label className="text-sm font-medium">Notes (Optional)</label>
                <Input
                  placeholder="Add notes about this campaign..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={200}
                />
              </div>
              {!link.isDefault && (
                <div className="flex flex-col items-center gap-1">
                  <label className="text-sm font-medium">Active</label>
                  <Switch checked={isActive} onCheckedChange={setIsActive} />
                </div>
              )}
            </div>

          </div>

          <div className="flex gap-3 pt-2">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)} 
              className="flex-1 h-11"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!name.trim() || updateLink.isPending}
              className="flex-1 h-11 bg-buy hover:bg-buy/90 text-white"
            >
              {updateLink.isPending ? (
                <>
                  <Loader2Icon className="size-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
  );
};

